import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Icon } from "./Icon";
import { ResourcePanel } from "./Resources";
import { ActivityHelp } from "./ActivityHelp";
import { useI18n } from "../lib/i18n";
import { narrationEnabled, playNarration, stopNarration, type NarrationClip } from "../lib/narration";
import { teachingLanguageNames, type Activity } from "../types";
import type { PlannedMoment } from "./Today";

/** Close on Escape. Shared by both modals so the behaviour cannot diverge. */
export function useEscape(onClose: () => void) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
}

/**
 * The line the parent says out loud.
 *
 * When the activity teaches a language the parent is not reading in — an
 * English phonics activity opened by an Urdu-reading parent — both texts are
 * shown: the original to say, the translation to understand. Rendering only the
 * translation would have quietly replaced the lesson with a different one.
 */
function SpokenLine({ activity }: { activity: Activity }) {
  const { t, locale } = useI18n();
  const taught = activity.teaching_language;
  const original = activity.original?.say_this;

  const showsBoth =
    taught !== "any" && taught !== locale && Boolean(original) && original !== activity.say_this;

  if (!showsBoth) return <blockquote>“{activity.say_this}”</blockquote>;

  return (
    <>
      <blockquote lang={taught} dir={taught === "en" ? "ltr" : "rtl"} className={taught === "en" ? "latin" : ""}>
        “{original}”
      </blockquote>
      <p className="spoken-gloss">
        <Icon name="message" size={14} />{" "}
        {t(taught === "en" ? "guide.inEnglish" : "guide.inUrdu")} — {activity.say_this}
      </p>
    </>
  );
}

/**
 * The parent's activity guide.
 *
 * `moment` is absent in preview: an admin opening this from the library has no
 * plan item to complete and no child to name, so the footer actions are dropped
 * and the learner's name becomes a placeholder. Everything above the footer is
 * the SAME component the parent sees — a preview rendered by a second, similar
 * component would drift away from the real one the first time either changed,
 * which is the whole reason for previewing at all.
 */
export function ActivityModal({
  activity,
  moment,
  learnerName,
  supabase,
  clip,
  onClose,
  onComplete,
  onEvidence,
}: {
  activity: Activity;
  moment?: PlannedMoment;
  learnerName: string;
  supabase: SupabaseClient;
  /** The spoken summary for this activity, already signed. Absent = no clip. */
  clip?: NarrationClip;
  onClose: () => void;
  onComplete?: () => void;
  onEvidence?: () => void;
}) {
  const { t, locale } = useI18n();
  useEscape(onClose);
  const complete = moment?.item.status === "done";
  const isPreview = !moment;

  // Collapsed by default. The guide is meant to be read standing up with a
  // child waiting, and four numbered steps followed by an embedded video is not
  // that — so the material is one deliberate tap away, announced by a count so
  // the parent knows there is something there.
  // Narration. `playing` only tracks whether the browser accepted the play, so
  // that a refusal shows a button rather than a control that does nothing.
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!clip || !narrationEnabled()) return;
    let cancelled = false;
    void playNarration(clip.url).then((started) => {
      if (!cancelled) setPlaying(started);
    });
    return () => {
      cancelled = true;
      stopNarration();
      setPlaying(false);
    };
    // Only when the clip changes: re-running on every render would restart the
    // audio under a parent who is listening to it.
  }, [clip]);

  // Step pictures, drawn for the activities that have them. Loaded as their
  // own chunk: the drawings are for the guide, and nobody should download
  // them to see the home page.
  const [pictures, setPictures] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setPictures(null);
    void import("../illustrations")
      .then(({ stepPictures, stepLines }) => {
        if (!cancelled) setPictures(stepPictures(activity.slug, stepLines(activity.what_to_do)));
      })
      .catch(() => { /* Pictures are an extra; the words are the guide. */ });
    return () => { cancelled = true; };
  }, [activity.slug, activity.what_to_do]);

  const [showResources, setShowResources] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const resourcesRef = useRef<HTMLDivElement | null>(null);
  const resourceCount = activity.resources.length;
  const exampleCount = activity.resources.filter((resource) => resource.is_example).length;

  function revealResources() {
    setShowResources(true);
    // After paint, so the panel exists to scroll to.
    window.requestAnimationFrame(() =>
      resourcesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
    );
  }

  const taughtIn =
    activity.teaching_language !== "any" && activity.teaching_language !== locale
      ? teachingLanguageNames[activity.teaching_language]
      : "";

  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={activity.title}>
      <div className="modal-card glass">
        <button className="modal-close" onClick={onClose} aria-label={t("common.close")}>
          <Icon name="close" />
        </button>
        <div className="modal-heading">
          <span className={`icon-tile ${activity.color}`}>
            <Icon name={activity.icon} size={24} />
          </span>
          <div>
            <small>
              {activity.area_label ?? activity.area} · {t("today.minutes", { count: activity.duration_minutes })}
              {taughtIn && <> · {t("guide.taughtIn", { language: taughtIn })}</>}
            </small>
            <h2>{activity.title}</h2>
            <p>{activity.prompt}</p>
            {/* Offered whether or not autoplay is on, and whether or not it was
                allowed to start: a parent who has narration switched off should
                still be able to ask for it once, and one whose browser refused
                the automatic play needs a way to hear it at all. */}
            {clip ? (
              <button
                type="button"
                className="narration-button"
                aria-pressed={playing}
                onClick={() => {
                  if (playing) {
                    stopNarration();
                    setPlaying(false);
                    return;
                  }
                  void playNarration(clip.url).then(setPlaying);
                }}
              >
                <Icon name="volume" size={14} />
                {t(playing ? "guide.narrationStop" : "guide.narrationPlay")}
              </button>
            ) : null}
          </div>
        </div>

        <div className="guide-grid">
          <div>
            <span className="guide-number">1</span>
            <div>
              <small>{t("guide.whatToDo")}</small>
              {/* The authored steps for THIS activity when they exist. The old
                  generic sentence remains the fallback rather than a blank, so
                  an activity nobody has written steps for still reads sensibly.
                  Authored steps are written one instruction per line. */}
              {activity.what_to_do ? (
                <ol className={pictures ? "guide-steps pictured" : "guide-steps"}>
                  {activity.what_to_do
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line, index) => (
                      <li key={index}>
                        {/* Built from constants in src/illustrations, never
                            from activity text, so innerHTML is safe here. */}
                        {pictures?.[index] && (
                          <span className="step-picture" dangerouslySetInnerHTML={{ __html: pictures[index] }} />
                        )}
                        <span>{line}</span>
                      </li>
                    ))}
                </ol>
              ) : (
                <p>{t("guide.whatToDoCopy", { name: learnerName })}</p>
              )}
            </div>
          </div>

          {activity.say_this && (
            <div>
              <span className="guide-number">2</span>
              <div>
                <small>{t("guide.whatToSay")}</small>
                <SpokenLine activity={activity} />
              </div>
            </div>
          )}

          {activity.observe_for && (
            <div>
              <span className="guide-number">3</span>
              <div>
                <small>{t("guide.whatToNotice")}</small>
                <p>{activity.observe_for}</p>
              </div>
            </div>
          )}

          {activity.evidence_hint && (
            <div>
              <span className="guide-number">4</span>
              <div>
                <small>{t("guide.evidence")}</small>
                <p>{t("guide.evidenceCopy", { hint: activity.evidence_hint })}</p>
              </div>
            </div>
          )}

          {/* Inside the instructions rather than under them: the moment a
              parent wonders what a worksheet or a worked example looks like is
              while they are reading the steps, not after they have scrolled
              past the outcomes. */}
          {resourceCount > 0 && (
            <div className="guide-resources">
              <span className="guide-number">
                <Icon name="folder" size={14} />
              </span>
              <div>
                <small>{t("guide.helpTitle")}</small>
                <p>
                  {exampleCount > 0
                    ? t("guide.helpWithExamples", {
                        count: resourceCount - exampleCount,
                        examples: exampleCount,
                      })
                    : t("guide.helpCopy", { count: resourceCount })}
                </p>
                <button className="link-button" onClick={() => (showResources ? setShowResources(false) : revealResources())}>
                  <Icon name={showResources ? "chevron" : "eye"} size={14} />{" "}
                  {showResources ? t("guide.hideHelp") : t("guide.seeHelp")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* The activity as it really goes.
            Collapsed, because a parent who has done this before wants the four
            steps and nothing else — but a parent doing it for the first time is
            usually not unsure of the instructions, they are unsure whether the
            way it went means it went wrong. A worked scene answers that. */}
        {activity.story && (
          <div className="guide-story">
            <button className="link-button" onClick={() => setShowStory((open) => !open)}>
              <Icon name={showStory ? "chevron" : "book"} size={14} /> {t("guide.storyToggle")}
            </button>
            {showStory && (
              <div className="guide-story-body">
                <small>{t("guide.storyTitle")}</small>
                {activity.story
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
              </div>
            )}
          </div>
        )}

        {(activity.child_move || activity.parent_move) && (
          <div className="separate-outcomes">
            {activity.child_move && (
              <div>
                <span>{t("guide.childOutcome")}</span>
                <strong>{activity.child_move}</strong>
              </div>
            )}
            {activity.parent_move && (
              <div>
                <span>{t("guide.parentPractice")}</span>
                <strong>{activity.parent_move}</strong>
              </div>
            )}
          </div>
        )}

        {/* Parent view only. An admin previewing the guide is checking how it
            reads, and spending a family's AI quota to do that would be odd. */}
        {!isPreview && <ActivityHelp activity={activity} />}

        {/* Mounted only when open, so a guide with three videos in it costs
            nothing to read until the parent asks for them. */}
        <div ref={resourcesRef}>
          {showResources && <ResourcePanel supabase={supabase} resources={activity.resources} />}
        </div>

        {isPreview ? (
          <div className="modal-actions preview-actions">
            <small>{t("admin.previewFooter")}</small>
            <button className="primary" onClick={onClose}>
              <Icon name="check" /> {t("common.close")}
            </button>
          </div>
        ) : onComplete || onEvidence ? (
          // Only what this account may actually do. A coach can read the guide
          // but not record for the child, and a button the database will refuse
          // is worse than no button.
          <div className="modal-actions">
            {onEvidence && (
              <button className="soft-button" onClick={onEvidence}>
                <Icon name="camera" /> {t("evidence.title")}
              </button>
            )}
            {onComplete && (
              <button className="primary" onClick={onComplete}>
                <Icon name="check" /> {complete ? t("guide.markNotDone") : t("guide.markComplete")}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
